/**
 * Config values: limits, URLs
 */

import Constants from 'expo-constants';

const WP_URL = process.env.EXPO_PUBLIC_WORDPRESS_URL || 'https://www.openstudiojazz.com';

const TRACKING_EVENTS_PATH = '/events';

const TRACKING_SERVICE_BASE_URLS = {
  production: 'https://video-tracking.openstudiojazz.com',
  staging: 'https://tq5zguxmcl.execute-api.us-east-1.amazonaws.com',
} as const;

function resolveTrackingServiceUrl(): string {
  if (process.env.EXPO_PUBLIC_TRACKING_SERVICE_URL) {
    return process.env.EXPO_PUBLIC_TRACKING_SERVICE_URL;
  }

  const appVariant = Constants.expoConfig?.extra?.appVariant;
  const baseUrl =
    appVariant === 'production'
      ? TRACKING_SERVICE_BASE_URLS.production
      : TRACKING_SERVICE_BASE_URLS.staging;

  return `${baseUrl.replace(/\/+$/, '')}${TRACKING_EVENTS_PATH}`;
}

export const LIMITS = {
  DEFAULT_LATEST: 10,
  DASHBOARD_LATEST: 13,
  COLLECTION_SEE_ALL: 50,
  SEARCH_RESULTS: 50,
} as const;

export const FLAGS = {
  // Continue section:
  // false => only dashboard + type3 + type4 courses
  // true  => dashboard + one item from every other course with progress
  CONTINUE_INCLUDE_ALL_COURSES: true,
} as const;

export const URLS = {
  WORDPRESS_BASE: WP_URL,
  /** In-app hub — native starting point (Home tab). */
  APP_HOME: '/courses',
  DASHBOARD: `${WP_URL}/dashboard/`,
  EXPLORE: `${WP_URL}/courses/`,
  EVENTS: `${WP_URL}/events/`,
  COMMUNITY: `${WP_URL}/community/`,
  WORKSHOP_REPLAYS_LIST: `${WP_URL}/community/c/workshops/`,
  WORKSHOP_REPLAYS_CURRENT_SEASON: `${WP_URL}/community/c/fall-26-blues/`,
  MY_ACCOUNT: `${WP_URL}/my-account/`,
  /**
   * Password resets go through WordPress: `wp-login` validates against WP and
   * re-syncs Supabase on the next sign-in, so changing it anywhere else would
   * silently drift.
   */
  LOST_PASSWORD: `${WP_URL}/my-account/lost-password/`,
  ORDERS: `${WP_URL}/my-account/orders/`,
  TERMS: `${WP_URL}/terms-of-service/`,
  PRIVACY: `${WP_URL}/privacy-policy/`,
  SUPPORT_EMAIL: 'mailto:support@openstudiojazz.com',
  JOIN: `${WP_URL}/join/`,
  instrument: (slug: string) => `${WP_URL}/instrument/${slug}/`,
  course: (slug: string) =>
    `${WP_URL.replace(/\/+$/, '')}/courses/${encodeURIComponent(slug)}/`,
} as const;

export const TRACKING_SERVICE_URL = resolveTrackingServiceUrl();

/**
 * Summer Sale 2026 — banner audience rules live in useSummerSaleBanner. All
 * instants are UTC/GMT (month is 0-indexed, so 6 = July):
 *   - production window: Jul 4 00:01 GMT → Jul 13 06:59 GMT
 *   - staging/dev window: same but starting Jul 1 00:00 GMT (early for testing)
 * FINAL_PHASE_START_MS flips the banner to the "Final 72 Hours" countdown
 * design (mirrors WP post 1601603 "Summer Sale Site Banner - Final 72 Hours
 * Timer"): production Jul 10 04:01 GMT (= 00:01 ET Friday Jul 10); staging in
 * the past so the new design is previewable immediately.
 */
const SUMMER_SALE_IS_PRODUCTION =
  Constants.expoConfig?.extra?.appVariant === 'production';

export const SUMMER_SALE = {
  // Everybody else (non-subscribers, expired subs, individual-course buyers…).
  URL: `${WP_URL.replace(/\/+$/, '')}/summer-sale-2026/`,
  // Monthly members: upgrade to annual at the sale price.
  UPGRADE_URL: `${WP_URL.replace(/\/+$/, '')}/switch-subscription`,
  START_MS: SUMMER_SALE_IS_PRODUCTION
    ? Date.UTC(2026, 6, 4, 0, 1, 0, 0)
    : Date.UTC(2026, 6, 1, 0, 0, 0, 0),
  FINAL_PHASE_START_MS: SUMMER_SALE_IS_PRODUCTION
    ? Date.UTC(2026, 6, 10, 4, 1, 0, 0)
    : Date.UTC(2026, 6, 9, 0, 0, 0, 0),
  // Matches the WP countdown target ("2026-07-13T06:59:00Z" = Jul 12 11:59pm
  // PT), so the banner disappears exactly when the clock hits zero.
  END_MS: Date.UTC(2026, 6, 13, 6, 59, 0, 0),
} as const;

// OS Pro Promo site banner, Fall '26 — mirrors WP Kadence conversion 1630456
// (gated on WP by client-mu-plugins/osj-pro-promo-banner-fall26.php).
// Audience: OS Regular members (any interval) + OS Pro monthly members; never
// Pro annual or Lifetime. Live Sept 28 – Oct 4 2026 inclusive, site timezone
// (America/Chicago, CDT = UTC-5), matching the WP gate.
export const PRO_PROMO_FALL26 = {
  URL: `${WP_URL.replace(/\/+$/, '')}/pro-special1/`,
  // Always the production upload (same asset the WP banner uses): the staging
  // WP doesn't have it, and the design should look identical on every env.
  IMAGE_URL: 'https://www.openstudiojazz.com/wp-content/uploads/2026/09/osprobanner-fall26.webp',
  START_MS: Date.UTC(2026, 8, 28, 5, 0, 0, 0), // 2026-09-28 00:00 CDT
  END_MS: Date.UTC(2026, 9, 5, 4, 59, 59, 999), // 2026-10-04 23:59:59 CDT
  // iOS: the App Store price cut on os_pro_annual ($559.99 → $399.00) could
  // only be scheduled from Sept 30 (Apple needs two days' notice), so the
  // native banner opens two days after the web one and closes with it.
  NATIVE_START_MS: Date.UTC(2026, 8, 30, 5, 0, 0, 0), // 2026-09-30 00:00 CDT
  // Final-days countdown (Ben's WP timer, 2026-10-01): replaces the "Limited
  // Time" pill from Oct 2 12:01am ET and counts down to Oct 4 11:59:59pm PT.
  // Same instants as the WP banners so all three tick in sync.
  COUNTDOWN_SHOW_FROM_MS: Date.UTC(2026, 9, 2, 4, 1, 0, 0), // 2026-10-02T04:01:00Z
  COUNTDOWN_TARGET_MS: Date.UTC(2026, 9, 5, 6, 59, 59, 0), // 2026-10-05T06:59:59Z
  // Reviewer accounts: always pass the audience rules (whatever their tier)
  // and their dismissal isn't persisted. On staging they also bypass the live
  // window (and see the countdown clock right away) so the design can be
  // checked now; on production they wait for the real dates like everyone else.
  PREVIEW_EMAILS: ['mihaidinu88@gmail.com', 'dm@openstudiojazz.com', 'ben@openstudiojazz.com'],
  PREVIEW_BYPASSES_WINDOW: !SUMMER_SALE_IS_PRODUCTION,
} as const;
